import React from "react";
import styled from "styled-components";
import Title from "common/Typography/Title";
import Text from "common/Typography/Text";
import LogoutOutlined from "@ant-design/icons/LogoutOutlined";
import SettingOutlined from "@ant-design/icons/SettingOutlined";
import Popover from "common/Popover/Popover";
import Button from "common/Buttons/Button";
import UserAvatar from "common/Avatars/UserAvatar";
import DarkModeToggle from "common/Home/Header/DarkModeToggle";
import useLogout from "utils/hooks/useLogout";
import { AuthUser } from "@shared/types";
import useMedia from "utils/hooks/useMedia";
import {
  IS_TELEGRAM_WEB_APP,
  MOBILE_SEARCH,
  SECURITY_SETTINGS_PATH,
} from "common/constants";
import Flex from "common/Boxes/Flex";
import { useHistory } from "react-router-dom";
import { useUserInfo } from "@shared/utils/hooks/misc";

interface UserInfoProps {
  user: AuthUser;
  toggleMobileMenu?: () => void;
}

const UserInfo: React.FC<UserInfoProps> = ({ user, toggleMobileMenu }) => {
  const [logout] = useLogout();
  const history = useHistory();
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const { userInfoItems } = useUserInfo();

  const popoverContent = (
    <>
      <TitleWrapper>
        <UserAvatar user={user} avatarSize={80} fallbackInitialsSize={1} />
        <Title level={3} style={{ marginTop: 12 }}>
          {user.email}
        </Title>
      </TitleWrapper>
      <Properties>
        {userInfoItems.map((item) => {
          return (
            <PropertyWrapper key={item.key}>
              <Text>{item.key}</Text>
              <Text type={item.type}>{item.value}</Text>
            </PropertyWrapper>
          );
        })}
      </Properties>
      <Properties>
        <PropertyWrapper>
          <DarkModeToggle />
        </PropertyWrapper>
      </Properties>
      <ButtonWrapper>
        {!IS_TELEGRAM_WEB_APP && (
          <StyledButton onClick={logout}>
            <LogoutOutlined />
            Logout
          </StyledButton>
        )}
        <StyledButton
          onClick={() => history.push(SECURITY_SETTINGS_PATH)}
          $colored={true}
        >
          <SettingOutlined />
          Settings
        </StyledButton>
      </ButtonWrapper>
    </>
  );

  const userAvatar = (
    <div style={{ cursor: "pointer" }} onClick={toggleMobileMenu}>
      <UserAvatar
        user={user}
        avatarSize={isMobile ? 32 : 40}
        fallbackInitialsSize={4}
      />
    </div>
  );

  if (toggleMobileMenu) {
    return userAvatar;
  }

  return (
    <Popover
      placement="bottomRight"
      title={false}
      content={popoverContent}
      trigger="click"
      styles={{ root: { width: 300 } }}
    >
      {userAvatar}
    </Popover>
  );
};

export default UserInfo;

const TitleWrapper = styled.div`
  padding: 16px 0 20px;
  display: flex;
  align-items: center;
  flex-direction: column;
  border-bottom: ${(p) => p.theme.borders.light};
`;
const Properties = styled.div`
  padding: 16px 8px;
  border-bottom: ${(p) => p.theme.borders.light};
`;
const PropertyWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;
const ButtonWrapper = styled(Flex)`
  width: 100%;
  position: relative;
  padding: 20px 0 10px;
  column-gap: 8px;
`;
const StyledButton = styled(Button)<{ $colored?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex: 1;
  ${(p) =>
    p.$colored &&
    `
    border-color: ${p.theme.colors.primaryBlue} !important;
    color: ${p.theme.colors.primaryBlue} !important;
  `}
`;
